import { DISK_UPLOAD_HASH_MAX_BYTES } from '../Contracts/DiskUpload'
import { isUnnamedPastedImage, pastedImageName, relativeDir } from './diskUploadPlan'

import type { DiskPickedFile } from '../Contracts/DiskUpload'

/**
 * Звідки менеджер завантажень бере файли: вибір файлів і папки, перетягування
 * з ОС, буфер обміну. Тут лише браузерні API, без стану.
 */

/** Файли з `<input type="file">`; для папки (`webkitdirectory`) — з відносним шляхом. */
export const filesFromInput = (list: FileList | null): DiskPickedFile[] =>
    Array.from(list ?? []).map((file) => ({ file, relativePath: relativeDir(file.webkitRelativePath ?? '') }))

/** Чи тягнуть файли з ОС (а не рядки диска чи текст). */
export const isFileDrag = (transfer: DataTransfer | null): boolean =>
    transfer !== null && Array.from(transfer.types).includes('Files')

const readEntries = (reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> =>
    new Promise((resolve, reject) => reader.readEntries(resolve, reject))

const entryFile = (entry: FileSystemFileEntry): Promise<File> =>
    new Promise((resolve, reject) => entry.file(resolve, reject))

/** Обхід перетягнутої папки: `readEntries` віддає порціями (≈100), тож читаємо до порожньої. */
const walk = async (entry: FileSystemEntry, path: string, out: DiskPickedFile[]): Promise<void> => {
    if (entry.isFile) {
        out.push({ file: await entryFile(entry as FileSystemFileEntry), relativePath: path })

        return
    }

    if (!entry.isDirectory) return

    const folder = path === '' ? entry.name : `${path}/${entry.name}`
    const reader = (entry as FileSystemDirectoryEntry).createReader()

    for (;;) {
        const batch = await readEntries(reader)
        if (batch.length === 0) break

        for (const child of batch) await walk(child, folder, out)
    }
}

/**
 * Файли й папки, кинуті з ОС. Записи (`webkitGetAsEntry`) треба взяти
 * синхронно, ще в обробнику `drop`: після першого `await` браузер очищає
 * `DataTransfer`. Без підтримки записів — лише плоскі файли.
 */
export const filesFromDrop = (transfer: DataTransfer): Promise<DiskPickedFile[]> => {
    const items = Array.from(transfer.items ?? []).filter((item) => item.kind === 'file')
    const entries = items.map((item) => (typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null))

    if (entries.length === 0 || entries.some((entry) => entry === null)) {
        return Promise.resolve(Array.from(transfer.files).map((file) => ({ file, relativePath: '' })))
    }

    return (async () => {
        const out: DiskPickedFile[] = []

        for (const entry of entries) {
            if (entry !== null) await walk(entry, '', out)
        }

        return out
    })()
}

/**
 * Файли з буфера обміну (Ctrl+V). Скриншот приходить як «image.png» —
 * такому зображенню даємо назву з датою й часом, щоб кілька вставок
 * не ставали «image (1).png», «image (2).png».
 */
export const filesFromClipboard = (data: DataTransfer | null, now: Date): DiskPickedFile[] => {
    if (data === null) return []

    const files = Array.from(data.items ?? [])
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((file): file is File => file !== null)
    const list = files.length > 0 ? files : Array.from(data.files ?? [])

    return list.map((file, index) => {
        if (!isUnnamedPastedImage(file)) return { file, relativePath: '' }

        // Кілька зображень за одну вставку — різні секунди в назві не потрібні, лише номер
        const base = pastedImageName(now, file.type)
        const name = index === 0 ? base : base.replace(/(\.[a-z]+)$/, ` ${index + 1}$1`)

        return { file: new File([file], name, { type: file.type, lastModified: now.getTime() }), relativePath: '' }
    })
}

let hashing: Promise<unknown> = Promise.resolve()

/**
 * sha256 файлу в hex через Web Crypto — лише до `DISK_UPLOAD_HASH_MAX_BYTES`
 * і по одному файлу за раз (весь файл лежить у пам'яті, поки рахується).
 * Без Web Crypto (не HTTPS) чи при помилці — `null`: бек приймає й без суми.
 */
export const fileSha256 = (file: Blob): Promise<string | null> => {
    if (file.size > DISK_UPLOAD_HASH_MAX_BYTES || typeof crypto === 'undefined' || !crypto.subtle) {
        return Promise.resolve(null)
    }

    const next = hashing.then(async () => {
        try {
            const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())

            return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
        } catch {
            return null
        }
    })

    hashing = next.catch(() => null)

    return next
}
